// TODO: Fields marked as `null` have not been checked
//       because of lack of sample data

import moment from 'moment';

import { CurrencyEnum, DocumentStatusEnum } from '../enums';
import { NullableNumber, NullableString } from '../util/types';

import {
  CapitalSubscriptionEnum,
  OnboardingQuestionMethod,
  SubscriptionStatusEnum,
} from './crm/types';
import {
  ENROLL_AUTH_REQUEST_TYPE,
  EnrollIndividualAddressRequestData,
  EnrollIndividualDocumentOwnRequestData,
  EnrollIndividualIdentityDetailRequestData,
  EnrollIndividualTaxDetailRequestData,
} from './enroll-requests';

export enum ENROLL_METHOD {
  getIndividualExtendedInfo,
  postEnrollmentOwnPersonal,
  postIndividualAddressOwn
}

export enum ENROLL_REQUEST_STATE {
  INITIAL,
  PENDING,
  READY,
  ERROR,
  REQUEST_ERROR,
  SERVER_ERROR,
}

export enum IndividualAddressTypeEnum {
  residential = 'R',
  mailing = 'M'
}

export type ConfirmedStepsData = {
  email?: boolean
  phone?: boolean
  didSendPhone?: boolean
}

export type CountryData = {
  id: string;
  name: string;
  phone_code: string;
  /**
   * The following properties are not a part of `/countries/list` call
   * but were present in `/countries`. Left here for reference if needed in the future.
   */
  alpha3_code?: string;
  numeric_code?: string;
  currency_code?: string;
  has_uk_us_eu_sanctions?: boolean;
  has_un_sanctions?: boolean
  has_fatf_warning?: boolean;
  is_black_listed?: boolean;
  ticpi_score?: number;
  is_eu_member?: boolean;
  is_in_euro_zone?: boolean;
  is_psd_member?: boolean;
}

export type SourceOfFundsAnswerData = {
  id?: NullableNumber
  source_of_fund_id?: number,
  proof_of_funds_id?: number,
  individual_id?: number,
  from?: string | number, // string when date, number when year
  to?: string | number, // string when date, number when year
  identity_name?: string,
  fund_type?: string,
  review_status?: string,
  review_summary?: NullableString,
}

export type IndividualFinancialQuestionnaire = {
  financial_questionnaire_id: number;
  financial_questionaire_type: string,
  currency: string;
}

export type IndividualExtendedInfoData = {
  id: number
  user_correlation_id: number
  individual_status: number
  user_status: number
  creation_stamp: string // "2021-02-05T07:53:56.1241971",
  email: string // "example@mail.com",
  email_confirmed: boolean
  phone_number: string // "+1839233",
  phone_number_confirmed: boolean // "+1839233",
  first_name: string
  middle_name: string
  last_name: string
  birth_date: string // "1978-11-11T00:00:00",
  birth_place_id: string // "BG",
  birth_place_location: string // "London",
  gender: any // { "key": "M", "value": "Male" },
  account_currency?: null
  purpose_of_trading_id: number
  annual_net_income_id: number
  estimated_net_worth_id: number
  estimated_liquid_worth_id: number
  mi_fid_category?: null
  trading_category: number
  bank_account_jurisdiction?: null
  has_financial_sanctions: boolean
  localized_detail?: null
  assigned_to_user?: null
  tenant_id: number
  type_behavior: number
  available_clients: string [] // "10nMobileLocal", "10nMobile", "10nWebLocal", "10nWeb"
  login_count?: number
  subscriptions?: Record<CapitalSubscriptionEnum, SubscriptionStatusEnum> // {"Capital": 0 }
  enroll_channel: string // "M"
  pep_detail?: null
  initial_deposit_id: number
  identity_details: EnrollIndividualIdentityDetailRequestData[]
  nationalities: string[] // "BG"
  alias: string // "SN"
  rebates_collector_account: string // the trading acc id is an example of this value
  manual_risk_classification: number
  tax_details: EnrollIndividualTaxDetailRequestData
  addresses: EnrollIndividualAddressRequestData[]
  documents: EnrollIndividualDocumentOwnRequestData[]
  source_of_incomes: any[] // see individualExtendedInfo.json for sample data
  source_of_funds: SourceOfFundsAnswerData[]
  individual_risks: any[] // see individualExtendedInfo.json for sample data
  individual_financial_questionnaires?: IndividualFinancialQuestionnaire[]
  initially_selected_plan_id?: string | number,
  profile_name?: string,
}

export class IndividualExtendedInfo {
  private data: IndividualExtendedInfoData;

  constructor(data: IndividualExtendedInfoData) {
    this.data = data;
  }

  getAddressByType(type: string): EnrollIndividualAddressRequestData {
    return (
      this
        .data
        .addresses
        .find(
          (address: EnrollIndividualAddressRequestData) => address.address_type === type,
        ) ?? {}
    ) as EnrollIndividualAddressRequestData;
  }

  getLatestApprovedDocumentByType(type: string): EnrollIndividualDocumentOwnRequestData {
    return this.data.documents
      .slice()
      .sort((
        previous: EnrollIndividualDocumentOwnRequestData,
        next: EnrollIndividualDocumentOwnRequestData,
      ) => moment(next.upload_stamp).unix() - moment(previous.upload_stamp).unix())
      .find((document: EnrollIndividualDocumentOwnRequestData) => (document.status === DocumentStatusEnum.Approved)
          && (document.document_type === type)) as EnrollIndividualDocumentOwnRequestData;
  }
}

export type EnrollCRMState = {
  country: CountryData[];
  initiallySelectedPlanId?: NullableNumber;
  initiallySelectedAutoPass?: boolean;
  initiallySelectedReferralCode?: NullableString;
  bankAccountDetailsOnCreate: {
    account_info: {
      bank_name: string;
      bank_country_id: string;
      bic: string;
      iban: string;
      currency_code: string;
    };
  };
};

export type EnrollAuthDataState = {
  lastType?: ENROLL_AUTH_REQUEST_TYPE
  status?: ENROLL_REQUEST_STATE // TODO: Rename to status
  email?: string
  userId?: number
  emailToConfirm?: string
  confirmedSteps: ConfirmedStepsData
  clients?: string[]
  firstName?: NullableString
  lastName?: NullableString
  country?: NullableString
  phoneCode?: NullableString
  phoneNumber?: NullableString
  statusCode?: number,
  errorCode?: NullableString,
  errorDescription?: NullableString,
  requestCallName?: string,
  isAuthLinkExpired?: boolean,
  referrerClientId?: NullableString,
  referralCode?: NullableString,
  planId?: NullableNumber,
  autoPass?: boolean,
}

export type EmploymentStatus = {
  id: number;
  name: string;
  business_required: boolean;
  order: number;
}


export enum IndividualStatusEnum {
    New = 100,
    Pending = 101,
    PendingAcceptance = 102,
    ForVerification = 103,
    OnHold = 104,
    Verified = 105,
    Blocked = 106,
    Active = 200,
    Terminated = 300,
    Declined = 400,
    Dormant = 500
}
